import type { Quality } from '@wyn/content';
import type { Rng } from '../rng.ts';

export type FxTone = 'good' | 'gold' | 'bad' | 'muted' | 'blue';

/** A moment worth a flourish on screen ("Clean cut!"). Display only; never affects the result. */
export interface Fx {
  tick: number;
  text: string;
  tone: FxTone;
  /** Engine-specific cue for the renderer (chips flying, the float plunging). */
  cue?: string;
  /** How hard the cue lands, 0–1. */
  power?: number;
}

/** What an engine reports when an attempt ends. Settlement turns it into XP, items and coin. */
export interface MinigameResult {
  /** 0–1. Drives XP and, for graded goods, quality. */
  score: number;
  title: string;
  text: string;
  breakdown: [label: string, value: number][];
  /**
   * What the attempt yields. Graded goods get their quality from the score; a fixed `quality`
   * (an unsold ware, an unbroken lockpick handed back) overrides it, and `bonus` is added to the
   * quality score of a graded good (a whopper).
   */
  yield: { itemKey: string; quantity: number; graded: boolean; quality?: Quality; bonus?: number } | null;
  /** Coin paid out by the venue (dice winnings). */
  coin: number;
  /** Multiplier on the XP payout; 0 for a tree left standing. */
  xpMult: number;
  /**
   * Notable things done in the attempt, good or bad ("all-ore", "cave-in"), by the engine's own
   * names (see FEATS). Deeds read them; they never change the payout.
   */
  feats?: string[];
}

export interface SettledYield {
  itemKey: string;
  quality: Quality;
  quantity: number;
}

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** The ware a player brought to the table (haggling), worth `value` coin at its quality. */
export interface Ware {
  itemKey: string;
  quality: Quality;
  value: number;
}

export interface InitContext {
  ware: Ware | null;
}

/** What a turn-based move may use besides the state: the session RNG and how long the player took. */
export interface MoveContext {
  rng: Rng;
  /** Real time since the previous move (or the start). Timed moves must fit inside it. */
  elapsedMs: number;
  level: number;
  ware: Ware | null;
}

/**
 * A turn-based engine. Pure: `move` returns the next state (throwing GameError on an illegal move),
 * `view` is all the client may see, and `result` is non-null once the attempt is over.
 */
export interface TurnEngineImpl<A, S, M, V> {
  init(a: A, level: number, serverSeed: string, ctx: InitContext): S;
  move(a: A, s: S, m: M, ctx: MoveContext): S;
  view(a: A, s: S): V;
  result(a: A, s: S): MinigameResult | null;
}
